import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { cognitoConfig } from './cognito-config';

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.clientId,
});

export interface AuthUser {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  name: string;
  groups: string[];
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: resolve,
      onFailure: reject,
      newPasswordRequired: () => reject(new Error('New password required')),
    });
  });
}

export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) cognitoUser.signOut();
  localStorage.removeItem('abp_oauth_tokens');
}

export function startSSOLogin(): void {
  const redirectUri = window.location.origin;
  const ssoUrl =
    `https://${cognitoConfig.domain}/oauth2/authorize?` +
    `identity_provider=AWSIdentityCenter&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `response_type=code&` +
    `client_id=${cognitoConfig.clientId}&` +
    `scope=email+openid+profile`;
  window.location.href = ssoUrl;
}

export async function handleOAuthCallback(code: string): Promise<void> {
  const redirectUri = window.location.origin;
  const tokenEndpoint = `https://${cognitoConfig.domain}/oauth2/token`;

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognitoConfig.clientId,
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) throw new Error('Token exchange failed');
  const tokens = await response.json();
  localStorage.setItem('abp_oauth_tokens', JSON.stringify(tokens));
}

export function getCurrentUser(): Promise<AuthUser | null> {
  const oauthStr = localStorage.getItem('abp_oauth_tokens');
  if (oauthStr) {
    try {
      const tokens = JSON.parse(oauthStr);
      const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
      return Promise.resolve({
        id: payload.sub,
        email: payload.email || '',
        givenName: payload.given_name || '',
        familyName: payload.family_name || '',
        name: `${payload.given_name || ''} ${payload.family_name || ''}`.trim() || payload.email || '',
        groups: payload['cognito:groups'] || [],
      });
    } catch {
      localStorage.removeItem('abp_oauth_tokens');
    }
  }

  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) { resolve(null); return; }

      cognitoUser.getUserAttributes((err, attrs) => {
        if (err) { reject(err); return; }
        const get = (name: string) => attrs?.find((a) => a.Name === name)?.Value || '';
        const groups = (session.getIdToken().payload['cognito:groups'] as string[]) || [];
        resolve({
          id: get('sub'),
          email: get('email'),
          givenName: get('given_name'),
          familyName: get('family_name'),
          name: `${get('given_name')} ${get('family_name')}`.trim() || get('email'),
          groups,
        });
      });
    });
  });
}

export function getAuthTokens(): AuthTokens | null {
  const oauthStr = localStorage.getItem('abp_oauth_tokens');
  if (oauthStr) {
    try {
      const t = JSON.parse(oauthStr);
      return { idToken: t.id_token, accessToken: t.access_token, refreshToken: t.refresh_token || '' };
    } catch { return null; }
  }
  return null;
}

/**
 * Resolve the current ID token (JWT) for authed API calls — works for both the
 * OAuth/SSO flow (abp_oauth_tokens) and password sign-in (Cognito session).
 */
export function getIdToken(): Promise<string | null> {
  const oauth = getAuthTokens();
  if (oauth?.idToken) return Promise.resolve(oauth.idToken);

  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return Promise.resolve(null);
  return new Promise((resolve) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) { resolve(null); return; }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}
